import {
    createListenerMiddleware,
    type ListenerEffectAPI,
    type ListenerErrorHandler,
    type ThunkDispatch,
    type TypedStartListening,
    type UnknownAction,
} from '@reduxjs/toolkit';

import { error } from '@pressreader/logger';

// The shared store is untyped (vanilla createStore — no app-wide RootState), so listener state is
// `unknown` and dispatch is typed to accept thunks over any state — notably RTK Query's
// `util.updateQueryData` cache thunks, whose own RootState requirement can't line up here (thunk
// state is contravariant, so it must be `any`, not `unknown`).
export type AppThunkDispatch = ThunkDispatch<any, unknown, UnknownAction>;

/** The `listenerApi` handed to `startAppListening` effects — for typing helpers that take it as a parameter. */
export type AppListenerEffectApi = ListenerEffectAPI<unknown, AppThunkDispatch>;

/**
 * Routes uncaught listener errors to the app logger. RTK already isolates a throwing effect (the store
 * and sibling listeners are unaffected, and cancellations — `TaskAbortError` — never reach `onError`);
 * this hook only redirects what would otherwise go to `console.error`, and covers effect AND predicate
 * throws for every listener app-wide — individual effects need no try/catch wrapper for logging.
 * Exported so test harnesses can wire the same handler into their own `createListenerMiddleware`.
 */
export const listenerOnError: ListenerErrorHandler = (err, errorInfo) => {
    error(`Unhandled listener ${errorInfo.raisedBy} error`, err);
};

// RTK's listener middleware — the lightweight, saga-free way to run an effect in response to a
// dispatched action. Wired into StoreManager once (see store.manager.ts); features register their
// own listeners via `startAppListening` (e.g. savedsearch reconciles its RTK Query cache on auth
// changes, replacing what used to be a reconcile saga).
export const listenerMiddleware = createListenerMiddleware<unknown, AppThunkDispatch>({ onError: listenerOnError });

// Effects run before sagas are told the action happened, so never dispatch one a saga awaits — see the
// RTKQ guide § "UI orchestration → listener middleware" and store.manager.listener.ordering.spec.ts.
// Annotate explicitly so the emitted .d.ts is a clean `TypedStartListening<…>` reference. Without it,
// TS expands the inferred listener type into a structural form whose declaration emit is malformed
// (a type predicate with `infer … extends …`) and fails to parse in downstream builds.
export const startAppListening: TypedStartListening<unknown, AppThunkDispatch> = listenerMiddleware.startListening;
